import React, { useState, useEffect } from 'react';
import {
  Calendar as CalendarIcon,
  Plus,
  ChevronLeft,
  ChevronRight,
  Filter,
  Settings,
  Users,
  Clock,
  MapPin,
  Video,
  Phone,
  Eye,
  Edit,
  Trash2,
  Check,
  X,
  AlertCircle,
  Share2
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  startOfDay,
  endOfDay,
  differenceInMinutes,
  isWithinInterval,
  parseISO
} from 'date-fns';
import { tr } from 'date-fns/locale';
import { useCalendar, CalendarEvent, Calendar as CalendarType } from '../../hooks/useCalendar';
import { useAuth } from '../../contexts/AuthContext';
import AddEventModal from '../Modals/AddEventModal';
import EditEventModal from '../Modals/EditEventModal';
import CreateCalendarModal from '../Modals/CreateCalendarModal';
import ShareCalendarModal from '../Modals/ShareCalendarModal';
import CalendarInvitationsModal from '../Modals/CalendarInvitationsModal';
import EventDetailModal from '../Modals/EventDetailModal';

// Theme colors mapping
const THEME_COLORS: Record<string, string> = {
  'Fiziki Randevu': '#BE185D', // Pink/Red
  'Online Randevu': '#10B981', // Green
  'Etkinlik': '#818CF8', // Light Blue/Indigo
  'Telefon Görüşmesi': '#F87171', // Coral/Light Red
  'Toplantı': '#2563EB', // Dark Blue
  'Vize Randevusu': '#F97316', // Orange
  'Öğlen Arası': '#8B5CF6', // Purple
  'İptal Olan Randevu': '#DC2626', // Red
};

const Calendar: React.FC = () => {
  const { user } = useAuth();
  const { events, calendars, loading, error, fetchCalendars, fetchEvents, createEvent, updateEvent, deleteEvent, updateAttendeeResponse, createCalendar, shareCalendar } = useCalendar();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [showEventDetailModal, setShowEventDetailModal] = useState(false);
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [showCreateCalendarModal, setShowCreateCalendarModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [visibleCalendars, setVisibleCalendars] = useState<string[]>([]);
  const [showCalendarSettings, setShowCalendarSettings] = useState(false);
  const [selectedCalendarForSharing, setSelectedCalendarForSharing] = useState<CalendarType | null>(null);
  const [showShareCalendarModal, setShowShareCalendarModal] = useState(false);
  const [showInvitationsModal, setShowInvitationsModal] = useState(false);
  const [calendarInvitations, setCalendarInvitations] = useState<any[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<{ date: string; startTime: string; endTime: string } | null>(null);

  // Initialize visible calendars
  useEffect(() => {
    if (calendars.length > 0) {
      setVisibleCalendars(calendars.filter(c => c.is_visible).map(c => c.id));
    }
  }, [calendars]);

  // Navigation Logic
  const handlePrevious = () => {
    if (viewMode === 'month') setCurrentDate(subMonths(currentDate, 1));
    else if (viewMode === 'week') setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subDays(currentDate, 1));
  };

  const handleNext = () => {
    if (viewMode === 'month') setCurrentDate(addMonths(currentDate, 1));
    else if (viewMode === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, 1));
  };

  const handleToday = () => setCurrentDate(new Date());

  // Handle empty time slot click
  const handleTimeSlotClick = (date: Date, hour: number) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    const start = `${hour.toString().padStart(2, '0')}:00`;
    const endHour = hour + 1;
    const end = `${endHour.toString().padStart(2, '0')}:00`;
    const finalEnd = endHour >= 24 ? '23:59' : end;

    setSelectedSlot({ date: formattedDate, startTime: start, endTime: finalEnd });
    setShowAddEventModal(true);
  };

  // Get events for current view
  const getEventsForDate = (date: Date): CalendarEvent[] => {
    return events.filter(event => {
      if (!visibleCalendars.includes(event.calendar_id)) return false;
      const eventDate = new Date(event.start_time);
      return isSameDay(eventDate, date);
    });
  };

  // Get Grid Days
  const getGridDays = () => {
    if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: weekStart, end: weekEnd });
    } else {
      return [currentDate];
    }
  };

  const gridDays = getGridDays();
  const weekDays = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

  // Handle event creation
  const handleCreateEvent = async (eventData: any) => {
    try {
      let calendarId = eventData.calendar_id;
      if (!calendarId) {
        const defaultCalendar = calendars.find(c => c.is_default && c.owner_id === user?.id);
        if (!defaultCalendar) {
          alert('Varsayılan takvim bulunamadı');
          return;
        }
        calendarId = defaultCalendar.id;
      }

      const attendees = eventData.attendees || [];

      // Ensure start_time and end_time are ISO strings if they aren't already
      // The modal usually handles this, but safety first.

      const newEventData = {
        calendar_id: calendarId,
        title: eventData.title,
        description: eventData.description,
        start_time: eventData.start_ts || eventData.start_time,
        end_time: eventData.end_ts || eventData.end_time,
        all_day: eventData.all_day || false,
        location: eventData.location,
        meeting_link: eventData.meeting_link || eventData.meetingLink,
        createGoogleMeet: eventData.createGoogleMeet,
        attendees: attendees,
        sendEmailInvites: eventData.sendEmailInvites,
        emailMessage: eventData.emailMessage,
        meeting_type: eventData.meeting_type,
        event_theme: eventData.event_theme,
        guest_name: eventData.guest_name,
        guest_surname: eventData.guest_surname,
        guest_class: eventData.guest_class,
        guest_phone: eventData.guest_phone,
        guest_email: eventData.guest_email,
        interest_department: eventData.interest_department,
        notes: eventData.notes
      };

      const { data, error } = await createEvent(newEventData);

      if (error) {
        alert(`Etkinlik oluşturulurken hata: ${error}`);
        return;
      }

      if (eventData.sendEmailInvites && attendees.length > 0) {
        alert(`Etkinlik başarıyla oluşturuldu! ${attendees.length} katılımcıya email daveti gönderildi.`);
      } else {
        alert('Etkinlik başarıyla oluşturuldu!');
      }
      setShowAddEventModal(false);
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Etkinlik oluşturulurken hata oluştu');
    }
  };

  // Handle event update
  const handleUpdateEvent = async (eventData: any) => {
    if (!selectedEvent) return;

    try {
      // Fix: Use ISO string conversion to avoid timezone shifts
      // eventData.date is YYYY-MM-DD, startTime is HH:mm
      // Constructing string 'YYYY-MM-DDTHH:mm:00' and parsing with new Date() creates a LOCAL date object
      // Calling toISOString() converts that LOCAL time to UTC, which is what we want to store
      const startISO = new Date(`${eventData.date}T${eventData.startTime}:00`).toISOString();
      const endISO = new Date(`${eventData.date}T${eventData.endTime}:00`).toISOString();

      const { error } = await updateEvent(selectedEvent.id, {
        calendar_id: eventData.calendar_id,
        title: eventData.title,
        description: eventData.description,
        start_time: startISO,
        end_time: endISO,
        location: eventData.location,
        meeting_link: eventData.meetingLink,
        event_theme: eventData.event_theme,
        guest_name: eventData.guest_name,
        guest_surname: eventData.guest_surname,
        guest_class: eventData.guest_class,
        guest_phone: eventData.guest_phone,
        guest_email: eventData.guest_email,
        interest_department: eventData.interest_department,
        notes: eventData.notes
      });

      if (error) {
        alert(`Etkinlik güncellenirken hata: ${error}`);
        return;
      }

      alert('Etkinlik başarıyla güncellendi!');
      setShowEditEventModal(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Etkinlik güncellenirken hata oluştu');
    }
  };

  // Handle event deletion
  const handleDeleteEvent = async (eventId: string) => {
    try {
      const { error } = await deleteEvent(eventId);
      if (error) {
        alert(`Etkinlik silinirken hata: ${error}`);
        return;
      }
      alert('Etkinlik başarıyla silindi!');
      setShowEditEventModal(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Etkinlik silinirken hata oluştu');
    }
  };

  // Handle attendee response
  const handleAttendeeResponse = async (eventId: string, response: string) => {
    try {
      const { error } = await updateAttendeeResponse(eventId, response);
      if (error) {
        alert(`Yanıt güncellenirken hata: ${error}`);
        return;
      }
      alert(`Yanıtınız "${response === 'accepted' ? 'Kabul' : response === 'declined' ? 'Red' : 'Belki'}" olarak kaydedildi!`);
    } catch (error) {
      console.error('Error updating response:', error);
      alert('Yanıt güncellenirken hata oluştu');
    }
  };

  // Toggle calendar visibility
  const toggleCalendarVisibility = (calendarId: string) => {
    setVisibleCalendars(prev =>
      prev.includes(calendarId)
        ? prev.filter(id => id !== calendarId)
        : [...prev, calendarId]
    );
  };

  // Handle calendar creation
  const handleCreateCalendar = async (calendarData: any) => {
    try {
      const { data, error } = await createCalendar(calendarData);
      if (error) {
        alert(`Takvim oluşturulurken hata: ${error}`);
        return;
      }
      alert('Takvim başarıyla oluşturuldu!');
      setShowCreateCalendarModal(false);
      fetchCalendars();
    } catch (error) {
      console.error('Error creating calendar:', error);
      alert('Takvim oluşturulurken hata oluştu');
    }
  };

  // Handle calendar sharing
  const handleShareCalendar = (calendar: CalendarType) => {
    setSelectedCalendarForSharing(calendar);
    setShowShareCalendarModal(true);
  };

  // Handle sending invitation
  const handleSendInvitation = async (invitationData: any) => {
    if (!selectedCalendarForSharing) return;
    try {
      const { error } = await shareCalendar(
        selectedCalendarForSharing.id,
        invitationData.userId,
        invitationData.permissionLevel
      );
      if (error) {
        alert(`Davet gönderilirken hata: ${error}`);
        return;
      }
      alert('Takvim başarıyla paylaşıldı!');
      setShowShareCalendarModal(false);
      setSelectedCalendarForSharing(null);
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert('Davet gönderilirken hata oluştu');
    }
  };

  const loadInvitations = async () => {
    setCalendarInvitations([]);
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    alert('Davet kabul edildi!');
    loadInvitations();
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    alert('Davet reddedildi!');
    loadInvitations();
  };

  // Helper to calculate event position for Week/Day view
  const calculateEventPosition = (event: CalendarEvent, dayEvents: CalendarEvent[]) => {
    const start = new Date(event.start_time);
    const end = new Date(event.end_time);

    // Sadece 09:00 - 00:00 aralığını gösteriyoruz (15 saat = 900 dakika)
    const DISPLAY_START_HOUR = 9;
    const TOTAL_DISPLAY_MINUTES = 15 * 60; // 900

    let startMinutes = (start.getHours() - DISPLAY_START_HOUR) * 60 + start.getMinutes();
    let durationMinutes = differenceInMinutes(end, start);

    // Takvim algoritması: Çakışan etkinlikleri grupla ve sütunlara ayır
    const sortedEvents = [...dayEvents].sort((a, b) => {
      const startA = new Date(a.start_time).getTime();
      const startB = new Date(b.start_time).getTime();
      return startA - startB || new Date(b.end_time).getTime() - new Date(a.end_time).getTime();
    });

    let group: CalendarEvent[] = [];
    let groupEnd = 0;

    for (const e of sortedEvents) {
      const eStart = new Date(e.start_time).getTime();
      const eEnd = new Date(e.end_time).getTime();

      if (group.length === 0) {
        group.push(e);
        groupEnd = eEnd;
      } else {
        if (eStart < groupEnd) {
          group.push(e);
          groupEnd = Math.max(groupEnd, eEnd);
        } else {
          if (group.some(g => g.id === event.id)) break;
          group = [e];
          groupEnd = eEnd;
        }
      }
    }

    const columns: CalendarEvent[][] = [];
    for (const e of group) {
      const eStart = new Date(e.start_time).getTime();
      let placed = false;
      for (const col of columns) {
        const lastInCol = col[col.length - 1];
        if (new Date(lastInCol.end_time).getTime() <= eStart) {
          col.push(e);
          placed = true;
          break;
        }
      }
      if (!placed) {
        columns.push([e]);
      }
    }

    const colIndex = Math.max(0, columns.findIndex(col => col.some(e => e.id === event.id)));
    const totalColumns = columns.length || 1;

    // Etkinlikleri yan yana sığdırmak için genişlik ve pozisyon hesapla
    const width = 95 / totalColumns;
    const left = colIndex * width;

    return {
      top: `${(startMinutes / TOTAL_DISPLAY_MINUTES) * 100}%`,
      height: `${(durationMinutes / TOTAL_DISPLAY_MINUTES) * 100}%`,
      left: `${left}%`,
      width: `${width}%`
    };
  };

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Takvim yükleniyor...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Hata Oluştu</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error}</p>
        <button
          onClick={() => { fetchCalendars(); fetchEvents(); }}
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Takvim</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Etkinliklerinizi planlayın ve takip edin
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowCalendarSettings(!showCalendarSettings)}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span>Ayarlar</span>
          </button>
          <button
            onClick={() => { setSelectedSlot(null); setShowAddEventModal(true); }}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Etkinlik Ekle</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Mini Calendar */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {format(currentDate, 'MMMM yyyy', { locale: tr })}
              </h3>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 text-xs">
              {weekDays.map(day => (
                <div key={day} className="text-center text-gray-500 dark:text-gray-400 py-1">
                  {day}
                </div>
              ))}
              {(() => {
                const mStart = startOfMonth(currentDate);
                const mEnd = endOfMonth(currentDate);
                const cStart = startOfWeek(mStart, { weekStartsOn: 1 });
                const cEnd = endOfWeek(mEnd, { weekStartsOn: 1 });
                const days = eachDayOfInterval({ start: cStart, end: cEnd });

                return days.map(day => {
                  // Logic to see if day has events (ignoring view filter for mini cal usually, but let's consistency)
                  // For performance, we might skip detailed event check or just check length > 0
                  const hasEvents = events.some(e => isSameDay(new Date(e.start_time), day) && visibleCalendars.includes(e.calendar_id));
                  const isToday = isSameDay(day, new Date());
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);

                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => { setSelectedDate(day); setCurrentDate(day); }}
                      className={`aspect-square text-xs rounded-lg transition-colors relative ${isToday
                        ? 'bg-blue-600 text-white'
                        : isCurrentMonth
                          ? 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-900 dark:text-white'
                          : 'text-gray-400 dark:text-gray-600'
                        } ${isSelected && !isToday ? 'ring-2 ring-blue-400' : ''}`}
                    >
                      {format(day, 'd')}
                      {hasEvents && (
                        <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full ${isToday ? 'bg-white' : 'bg-blue-500'}`} />
                      )}
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          {/* My Calendars */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Takvimlerim</h3>
              <button
                onClick={() => setShowCreateCalendarModal(true)}
                className="p-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white rounded-lg transition-colors"
                title="Yeni Takvim Oluştur"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2">
              {calendars.map(calendar => (
                <div key={calendar.id} className="flex items-center space-x-3 group">
                  <input
                    type="checkbox"
                    checked={visibleCalendars.includes(calendar.id)}
                    onChange={() => toggleCalendarVisibility(calendar.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: calendar.color }}
                  />
                  <span className="text-sm text-gray-900 dark:text-white flex-1">
                    {calendar.name}
                  </span>
                  {calendar.type === 'shared' && (
                    <Share2 className="w-3 h-3 text-gray-400" />
                  )}
                  {calendar.owner_id === user?.id && (
                    <button
                      onClick={() => handleShareCalendar(calendar)}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-all"
                      title="Takvimi Paylaş"
                    >
                      <Share2 className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Today's Events */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Bugünün Etkinlikleri</h3>
            {getEventsForDate(new Date()).length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Bugün etkinlik yok</p>
            ) : (
              <div className="space-y-3">
                {getEventsForDate(new Date()).map(event => (
                  <div key={event.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: (event.event_theme && THEME_COLORS[event.event_theme]) || event.calendar?.color || '#3B82F6' }}
                      />
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                        {event.title}
                      </h4>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                      <Clock className="w-3 h-3" />
                      <span>
                        {format(new Date(event.start_time), 'HH:mm')} - {format(new Date(event.end_time), 'HH:mm')}
                      </span>
                    </div>
                    {event.meeting_link && (
                      <button
                        onClick={() => window.open(event.meeting_link, '_blank')}
                        className="mt-2 flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs transition-colors"
                      >
                        <Video className="w-3 h-3" />
                        <span>Meet'e Katıl</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Calendar */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden min-h-[600px] flex flex-col">
            {/* Calendar Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handlePrevious}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {viewMode === 'day'
                    ? format(currentDate, 'd MMMM yyyy', { locale: tr })
                    : format(currentDate, 'MMMM yyyy', { locale: tr })
                  }
                </h2>
                <button
                  onClick={handleNext}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={handleToday}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
                >
                  Bugün
                </button>
                <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                  {['month', 'week', 'day'].map(mode => (
                    <button
                      key={mode}
                      onClick={() => setViewMode(mode as any)}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === mode
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                    >
                      {mode === 'month' ? 'Ay' : mode === 'week' ? 'Hafta' : 'Gün'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="p-0 flex-1 overflow-auto">
              {viewMode === 'month' ? (
                <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-800 border-l border-t bg-opacity-50">
                  {/* Week headers */}
                  {weekDays.map(day => (
                    <div key={day} className="bg-white dark:bg-gray-900 text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-3">
                      {day}
                    </div>
                  ))}

                  {gridDays.map(day => {
                    const dayEvents = getEventsForDate(day);
                    const isToday = isSameDay(day, new Date());
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    return (
                      <div
                        key={day.toISOString()}
                        onClick={() => setSelectedDate(day)}
                        className={`min-h-[120px] bg-white dark:bg-gray-900 p-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          } ${!isCurrentMonth ? 'bg-gray-50 dark:bg-gray-800/50 text-gray-400' : ''}`}
                      >
                        <div className="flex justify-between items-start">
                          <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white' : ''
                            }`}>
                            {format(day, 'd')}
                          </span>
                        </div>
                        <div className="mt-1 space-y-1">
                          {dayEvents.slice(0, 4).map(event => {
                            const calendar = calendars.find(c => c.id === event.calendar_id);
                            return (
                              <div
                                key={event.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEvent(event);
                                  setShowEventDetailModal(true);
                                }}
                                className="text-xs p-1 rounded text-white cursor-pointer hover:opacity-80 transition-opacity truncate"
                                style={{ backgroundColor: (event.event_theme && THEME_COLORS[event.event_theme]) || calendar?.color || '#3B82F6' }}
                                title={event.title}
                              >
                                {format(new Date(event.start_time), 'HH:mm')} {event.title}
                              </div>
                            );
                          })}
                          {dayEvents.length > 4 && (
                            <div className="text-xs text-gray-500 pl-1">
                              +{dayEvents.length - 4} daha
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Week and Day Views */
                <div className="flex h-full min-h-[800px]">
                  {/* Time Axis */}
                  <div className="w-16 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                    {Array.from({ length: 15 }).map((_, i) => {
                      const hour = i + 9;
                      return (
                        <div key={hour} className="h-20 text-xs text-gray-500 text-right pr-2 pt-1 border-b border-gray-100 dark:border-gray-800 relative">
                          <span className="-top-2 relative">{hour.toString().padStart(2, '0')}:00</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Columns */}
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols divide-x divide-gray-200 dark:divide-gray-800"
                    style={{ gridTemplateColumns: viewMode === 'week' ? 'repeat(7, 1fr)' : '1fr' }}
                  >
                    {gridDays.map(day => {
                      const dayEvents = getEventsForDate(day);
                      const isToday = isSameDay(day, new Date());

                      return (
                        <div key={day.toISOString()} className="relative bg-white dark:bg-gray-900 min-w-[100px]">
                          {/* Header for Week View */}
                          {viewMode === 'week' && (
                            <div className={`text-center py-2 border-b border-gray-200 dark:border-gray-800 ${isToday ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                              <div className="text-xs text-gray-500 uppercase">{format(day, 'EEE', { locale: tr })}</div>
                              <div className={`text-sm font-semibold ${isToday ? 'text-blue-600' : ''}`}>{format(day, 'd')}</div>
                            </div>
                          )}

                          {/* Event Area (Height = 15 * 80px = 1200px typically, but we use percentages for positioning relative to this container) */}
                          <div className="relative h-[1200px] overflow-hidden">
                            {/* Horizontal guidelines for hours */}
                            {Array.from({ length: 15 }).map((_, i) => {
                              const hour = i + 9;
                              return (
                                <div
                                  key={i}
                                  className="absolute w-full border-t border-gray-100 dark:border-gray-800 h-20 cursor-pointer hover:bg-blue-50/50 dark:hover:bg-blue-900/20 z-0"
                                  style={{ top: `${(i / 15) * 100}%` }}
                                  onClick={() => handleTimeSlotClick(day, hour)}
                                />
                              );
                            })}

                            {dayEvents.map(event => {
                              const style = calculateEventPosition(event, dayEvents);
                              const calendar = calendars.find(c => c.id === event.calendar_id);
                              return (
                                <div
                                  key={event.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedEvent(event);
                                    setShowEventDetailModal(true);
                                  }}
                                  className="absolute p-1 rounded border border-white/20 hover:z-10 cursor-pointer transition-all overflow-hidden"
                                  style={{
                                    top: style.top,
                                    height: style.height,
                                    left: style.left,
                                    width: style.width,
                                    backgroundColor: (event.event_theme && THEME_COLORS[event.event_theme]) || calendar?.color || '#3B82F6',
                                    color: '#fff'
                                  }}
                                >
                                  <div className="text-xs font-semibold truncate">
                                    {event.title}
                                  </div>
                                  <div className="text-[10px] opacity-90 truncate">
                                    {format(new Date(event.start_time), 'HH:mm')} - {format(new Date(event.end_time), 'HH:mm')}
                                  </div>
                                  {event.location && <div className="text-[10px] opacity-80 truncate">{event.location}</div>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Event Modal */}
      <AddEventModal
        isOpen={showAddEventModal}
        onClose={() => { setShowAddEventModal(false); setSelectedSlot(null); }}
        onSave={handleCreateEvent}
        calendars={calendars}
        defaultCalendarId={calendars.find(c => c.is_default && c.owner_id === user?.id)?.id}
        initialDate={selectedSlot?.date}
        initialStartTime={selectedSlot?.startTime}
        initialEndTime={selectedSlot?.endTime}
      />

      {/* Event Detail Modal */}
      {selectedEvent && (
        <EventDetailModal
          isOpen={showEventDetailModal}
          onClose={() => {
            setShowEventDetailModal(false);
            setSelectedEvent(null);
          }}
          event={selectedEvent}
          onEdit={() => {
            setShowEventDetailModal(false);
            setShowEditEventModal(true);
          }}
          onDelete={handleDeleteEvent}
        />
      )}

      {/* Edit Event Modal */}
      {selectedEvent && (
        <EditEventModal
          isOpen={showEditEventModal}
          onClose={() => {
            setShowEditEventModal(false);
            setSelectedEvent(null);
          }}
          onSave={handleUpdateEvent}
          onDelete={handleDeleteEvent}
          event={selectedEvent}
          calendars={calendars}
        />
      )}

      {/* Create Calendar Modal */}
      <CreateCalendarModal
        isOpen={showCreateCalendarModal}
        onClose={() => setShowCreateCalendarModal(false)}
        onSave={handleCreateCalendar}
      />

      {/* Share Calendar Modal */}
      {selectedCalendarForSharing && (
        <ShareCalendarModal
          isOpen={showShareCalendarModal}
          onClose={() => {
            setShowShareCalendarModal(false);
            setSelectedCalendarForSharing(null);
          }}
          calendar={selectedCalendarForSharing}
          onSendInvitation={handleSendInvitation}
        />
      )}

      {/* Calendar Invitations Modal */}
      <CalendarInvitationsModal
        isOpen={showInvitationsModal}
        onClose={() => setShowInvitationsModal(false)}
        invitations={calendarInvitations}
        onAccept={handleAcceptInvitation}
        onDecline={handleDeclineInvitation}
        onRefresh={loadInvitations}
      />

      {/* Calendar Settings Modal */}
      {showCalendarSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Takvim Ayarları</h3>
              <button
                onClick={() => setShowCalendarSettings(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Takvim Görünürlüğü</h4>
                  <div className="space-y-2">
                    {calendars.map(calendar => (
                      <div key={calendar.id} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: calendar.color }}
                          />
                          <span className="text-sm text-gray-900 dark:text-white">
                            {calendar.name}
                          </span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={visibleCalendars.includes(calendar.id)}
                            onChange={() => toggleCalendarVisibility(calendar.id)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;